import React, { useState, useEffect, useRef } from "react"
import { Link } from "gatsby"
import Helmet from "react-helmet"
import { Layout } from "../components/Layout"
import { SEO } from "../components/SEO"
import { AffiliateList } from "../components/AffiliateList"
import { CTABlock } from "../components/CTABlock"
import { ConsultingLocations } from "../components/ConsultingLocations"
import elizabethScrubs from "../assets/images/elizabeth-profile.jpg"
import elizabethSurgicalScrubs from "../assets/images/elizabeth-surgical-scrubs.avif"
import surgeryTheatre from "../assets/images/surgery.jpg"
import clinicHero from "../assets/images/about-hero.jpg"
import jamesPortrait from "../assets/images/james-portrait.jpg"
import elliePortrait from "../assets/images/headshot-ellie.avif"
import emilyPortrait from "../assets/images/headshot-emily.avif"
import catherinePortrait from "../assets/images/headshot-catherine.avif"
import starReviewGold from "../assets/images/star_review_gold.svg"
import containerClasses from "../styles/container.module.css"

// Verbatim excerpts from Google reviews that mention the team — the full
// reviews live on the treatment pages' review sections.
const teamReviews = [
  {
    quote:
      "From my first consultation to being signed off 6 weeks after surgery Dr Hawkes and her team have been excellent.",
    name: "Blepharoplasty Patient",
    when: "July 2025",
  },
  {
    quote:
      "On the day of my surgery, the team, led by Miss Hawkes, were excellent. I felt safe and was well cared for from arrival, through surgery and in recovery afterwards.",
    name: "Blepharoplasty Patient",
    when: "June 2025",
  },
  {
    quote:
      "Communications have been excellent too, both with Elizabeth and her PA, encouraging direct contact with WhatsApp to allay any worries during the recovery period.",
    name: "Jacqueline Crack",
    when: "April 2026",
  },
]

const teamMembers = [
  {
    id: 1,
    name: "Dr Elizabeth Hawkes",
    role: "Clinical Lead & Consultant Oculoplastic Surgeon",
    tag: "Clinical",
    image: elizabethScrubs,
    link: "/about/",
  },
  {
    id: 2,
    name: "Mr James Neffendorf",
    role: "Vitreoretinal Surgeon",
    tag: "Clinical",
    image: jamesPortrait,
    link: null,
  },
  {
    id: 3,
    name: "Ellie",
    role: "Practice Manager & Surgical Co-ordinator",
    tag: "Operations",
    image: elliePortrait,
    link: null,
  },
  {
    id: 4,
    name: "Emily",
    role: "Executive Assistant to Dr Elizabeth Hawkes",
    tag: "Operations",
    image: emilyPortrait,
    link: null,
    // Slight zoom anchored at the base so her head sits higher in frame
    imgStyle: { transform: "scale(1.1)", transformOrigin: "50% 100%" },
  },
  {
    id: 5,
    name: "Catherine Wilson",
    role: "Finance",
    tag: "Finance",
    image: catherinePortrait,
    link: null,
    // Source photo is a wide shot — crop in so her framing matches the rest
    imgStyle: { transform: "scale(1.45)", transformOrigin: "50% 70%" },
  },
]

const clinicalAreas = [
  {
    number: "01",
    title: "Oculoplastic Surgery",
    description:
      "Blepharoplasty, brow lift surgery, ptosis correction, chalazion (stye) removal and management, ectropion and entropion correction, eyelid lump diagnosis and removal, watery eye solutions, and xanthelasma removal.",
  },
  {
    number: "02",
    title: "Ophthalmology",
    description:
      "Dry eye management, cataract surgery, blepharitis assessment, BlephEx and recurrent stye treatment, under-eye bags, YAG laser surgery, and general and urgent ophthalmology.",
  },
  {
    number: "03",
    title: "Aesthetics",
    description:
      "Anti-wrinkle injections, dermal fillers, tear trough fillers, dark circle treatment, Profhilo, polynucleotides, Ameela exosomes, AQUAGOLD fine touch facials, platelet-rich plasma, Morpheus8, Sculptra, Clarius HD3 ultrasound, non-surgical brow lifts, facial fat transfer, and nanofat and stem cell rejuvenation.",
  },
]

const AboutClinic = () => {
  const statsRef = useRef(null)
  const teamScrollRef = useRef(null)
  const [teamIndex, setTeamIndex] = useState(0)

  const scrollTeam = dir => {
    const next = Math.max(0, Math.min(teamMembers.length - 1, teamIndex + dir))
    setTeamIndex(next)
    const container = teamScrollRef.current
    if (!container) return
    const card = container.querySelector("[data-card]")
    if (!card) return
    const cardWidth = card.offsetWidth + 24
    container.scrollTo({ left: next * cardWidth, behavior: "smooth" })
  }
  const [c1, setC1] = useState(0)
  const [c2, setC2] = useState(0)
  const [c3, setC3] = useState(0)
  const counted = useRef(false)
  const [revealed, setRevealed] = useState(() => new Set())

  useEffect(() => {
    const animate = (target, setter, duration = 1800) => {
      const start = performance.now()
      const easeOutCubic = t => 1 - Math.pow(1 - t, 3)
      const tick = now => {
        const elapsed = now - start
        const progress = Math.min(elapsed / duration, 1)
        const value = easeOutCubic(progress) * target
        setter(progress === 1 ? target : Math.floor(value))
        if (progress < 1) requestAnimationFrame(tick)
      }
      requestAnimationFrame(tick)
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !counted.current) {
          counted.current = true
          animate(20, setC1, 1800)
          animate(2, setC2, 1400)
          animate(3, setC3, 1600)
        }
      },
      { threshold: 0.3 }
    )
    if (statsRef.current) observer.observe(statsRef.current)
    return () => observer.disconnect()
  }, [])

  // Reveal each section as it scrolls into view. Revealed sections are tracked
  // in React state (keyed by data-reveal) and rendered as a data-visible
  // attribute, so re-renders (e.g. the per-frame stats counter) never strip the
  // reveal marker — which was making sections appear instantly with no fade.
  useEffect(() => {
    const els = Array.from(document.querySelectorAll("[data-reveal]"))
    const reveal = key =>
      setRevealed(prev => (prev.has(key) ? prev : new Set(prev).add(key)))
    if (typeof IntersectionObserver === "undefined") {
      els.forEach(el => reveal(el.dataset.reveal))
      return
    }
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            reveal(entry.target.dataset.reveal)
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0, rootMargin: "0px 0px -10% 0px" }
    )
    els.forEach(el => observer.observe(el))
    return () => observer.disconnect()
  }, [])

  // Props for a section that fades in on scroll (paired with .reveal-on-scroll)
  const revealProps = key => ({
    "data-reveal": key,
    "data-visible": revealed.has(key) ? "true" : undefined,
  })

  // ----- Structured data ------------------------------------------------
  // Employee list merges into the site-wide clinic and physician entities
  // via their shared @ids, so Google sees one graph.
  const teamSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalBusiness",
    "@id": "https://drelizabethhawkes.com/#clinic",
    name: "Dr Hawkes Clinic",
    employee: [
      {
        "@type": "Physician",
        "@id": "https://drelizabethhawkes.com/#physician",
        name: "Dr Elizabeth Hawkes",
        jobTitle: "Clinical Lead and Consultant Oculoplastic Surgeon",
        url: "https://drelizabethhawkes.com/about/",
      },
      {
        "@type": "Physician",
        name: "Mr James Neffendorf",
        jobTitle: "Vitreoretinal Surgeon",
      },
      {
        "@type": "Person",
        name: "Ellie",
        jobTitle: "Practice Manager and Surgical Co-ordinator",
      },
      {
        "@type": "Person",
        name: "Emily",
        jobTitle: "Executive Assistant to Dr Elizabeth Hawkes",
      },
      {
        "@type": "Person",
        name: "Catherine Wilson",
        jobTitle: "Finance",
      },
    ],
  }

  const aboutPageSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "@id": "https://drelizabethhawkes.com/team/#webpage",
    url: "https://drelizabethhawkes.com/team/",
    name: "Meet the Team at Dr Hawkes Clinic, London",
    description:
      "The specialist team behind Dr Hawkes Clinic: eye surgery, ophthalmology and facial aesthetics in London.",
    about: { "@id": "https://drelizabethhawkes.com/#clinic" },
  }

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://drelizabethhawkes.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Meet the Team",
        item: "https://drelizabethhawkes.com/team/",
      },
    ],
  }

  return (
    <Layout page="About" overlayNav>
      <SEO
        title="Meet the Team | Dr Hawkes Clinic, London"
        description="Meet the team at Dr Hawkes Clinic: Consultant Oculoplastic Surgeon Dr Elizabeth Hawkes, Vitreoretinal Surgeon Mr James Neffendorf, and the specialist support team behind our London clinic."
        pathname="/team/"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(teamSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(aboutPageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      {/* Hero — full-bleed designed asset: the wall is extended in the
          image itself, so the headline sits on clean light wall with dark
          text and no CSS compositing. Mobile stacks portrait over text. */}
      <div
        {...revealProps("hero")}
        className="relative overflow-hidden reveal-on-scroll lg:flex lg:items-end bg-white about-hero-height hero-mobile-nav-offset"
      >
        <div className="about-hero-imgwrap">
          <img
            src={clinicHero}
            alt="Dr Elizabeth Hawkes, Consultant Oculoplastic Surgeon, at the clinic"
            className="about-hero-img"
          />
        </div>
        <div className="about-hero-fade" aria-hidden="true" />

        {/* Text content — centred above the portrait on mobile (homepage
            pattern), left-aligned over the extended wall on desktop */}
        <div
          className={`${containerClasses.container} relative pt-20 pb-12 lg:pt-0 lg:pb-16 xl:pb-20`}
        >
          <div className="col-span-4 md:col-span-8 lg:col-span-6 text-center lg:text-left">
            <p
              className="mt-0 mb-3 font-mono text-xs uppercase text-gold600"
              style={{ letterSpacing: "0.2em" }}
            >
              Meet the team behind
            </p>
            <h1
              className="mt-0 mb-6 text-blackNew fluid-display-02"
              style={{ letterSpacing: "0.01em" }}
            >
              Dr Elizabeth Hawkes&nbsp;Clinic
            </h1>
            <div className="flex flex-col sm:flex-row sm:justify-center lg:justify-start items-center sm:items-stretch gap-3">
              <a
                className="inline-block w-full sm:w-auto text-center bg-blackNew text-white font-mono uppercase tracking-wider text-xs py-3 px-6 hover:bg-gold600 transition-colors duration-300"
                href="#team"
                onClick={e => {
                  // Gatsby's router resets scroll on hash navigation, so
                  // scroll directly and keep the URL untouched.
                  e.preventDefault()
                  const el = document.getElementById("team")
                  if (el) el.scrollIntoView({ behavior: "smooth" })
                }}
              >
                Meet the Team
              </a>
              <Link
                className="inline-block w-full sm:w-auto text-center border-1 border-solid border-blackNew text-blackNew font-mono uppercase tracking-wider text-xs py-3 px-6"
                to="/about/"
              >
                Meet Dr Hawkes
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Intro + Stats — editorial two-column intro (heading left, copy
          right), followed by three ruled stat cards: small label top-left,
          large figure right, rule, caption right-aligned */}
      <div
        {...revealProps("intro")}
        className="py-16 bg-white lg:py-24 reveal-on-scroll"
      >
        <div className={`${containerClasses.container}`}>
          <div className="col-span-4 md:col-span-8 lg:col-span-6">
            <p className="mt-0 overline">Dr Hawkes Clinic</p>
            <h2 className="mt-4 mb-6 lg:mb-0 fluid-heading-06">
              Eye surgery and facial aesthetics, practised with{" "}
              <i>precision and care</i>
            </h2>
          </div>
          <div className="col-span-4 md:col-span-8 lg:col-span-5 lg:col-start-8 about-intro-copy">
            <p className="mt-0 mb-0 text-gray-500 text-lg leading-relaxed">
              Founded by Consultant Oculoplastic Surgeon Dr Elizabeth Hawkes
              FRCOphth MBBS BSc, the clinic brings together surgical
              precision, clinical rigour and a deeply personalised approach to
              care. Patients are seen at the Cadogan Clinic in Chelsea and The
              London Lauriston Clinic for surgical and non-surgical treatment
              alike. From first consultation to final review, every patient is
              looked after by the same close-knit specialist team.
            </p>
          </div>

          <div
            ref={statsRef}
            className="col-span-4 md:col-span-8 lg:col-span-12 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mt-12 lg:mt-20"
          >
            {[
              {
                value: c1,
                suffix: "+",
                label: "Experience",
                caption: "Years of combined clinical experience across the team",
              },
              {
                value: c2,
                suffix: "",
                label: "Locations",
                caption:
                  "The Cadogan Clinic, Chelsea and The London Lauriston Clinic",
              },
              {
                value: c3,
                suffix: "",
                label: "Specialisms",
                caption: "Oculoplastic surgery, ophthalmology and aesthetics",
              },
            ].map(stat => (
              <div
                key={stat.label}
                className="flex flex-col border border-solid border-gray-300 p-8"
              >
                <p className="mt-0 mb-0 font-mono text-xs uppercase tracking-widest text-blackNew">
                  {stat.label}
                </p>
                <p className="mt-10 mb-6 text-right text-blackNew stat-number tabular-nums">
                  {stat.value}
                  {stat.suffix && (
                    <span className="text-gray-400">{stat.suffix}</span>
                  )}
                </p>
                <div className="mt-auto border-t border-solid border-gray-300 pt-4">
                  <p className="mb-0 text-sm text-right text-gray-500 leading-relaxed">
                    {stat.caption}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Clinical Areas */}
      <div
        {...revealProps("clinical")}
        className="py-16 bg-white lg:py-24 reveal-on-scroll"
      >
        <div className={`${containerClasses.container}`}>
          {/* Heading row */}
          <div className="col-span-4 md:col-span-8 lg:col-span-12 mb-10">
            <p className="mt-0 overline">Specialisms</p>
            <h2 className="mt-3 mb-0 fluid-heading-06">
              Three disciplines, <i>one dedicated clinic</i>
            </h2>
          </div>

          {/* Theatre image */}
          <div className="col-span-4 md:col-span-4 lg:col-span-6 self-stretch">
            <div
              className="relative w-full overflow-hidden bg-gray-100"
              style={{ aspectRatio: "1/1" }}
            >
              <img
                src={surgeryTheatre}
                alt="Dr Hawkes operating with a surgical microscope in theatre"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>

          {/* Specialisms list */}
          <div className="col-span-4 md:col-span-4 lg:col-span-5 lg:col-start-8 flex flex-col justify-center">
            {clinicalAreas.map((area, i) => (
              <div
                key={area.number}
                className={`py-4 ${
                  i < clinicalAreas.length - 1
                    ? "border-b border-solid border-gray-200"
                    : ""
                }`}
              >
                <div className="flex items-baseline gap-4">
                  <span className="font-mono text-xs text-gray-500 flex-shrink-0">
                    {area.number}
                  </span>
                  <div>
                    <h3 className="mt-0 mb-1 fluid-heading-04">{area.title}</h3>
                    <p className="mb-0 text-sm text-gray-500 leading-relaxed">
                      {area.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Meet the Team */}
      <div
        id="team"
        {...revealProps("team")}
        className="py-16 bg-white lg:py-24 overflow-hidden reveal-on-scroll"
        style={{ scrollMarginTop: "120px" }}
      >
        {/* Header row */}
        <div className={containerClasses.container}>
          <div className="col-span-4 md:col-span-6 lg:col-span-8 mb-10">
            <p className="mt-0 overline">The People</p>
            <h2 className="mt-2 fluid-heading-06">
              Meet <i>the Team</i>
            </h2>
            <p className="mt-4 mb-0 text-gray-500">
              A dedicated team of specialists committed to exceptional patient
              care.
            </p>
          </div>
          <div className="col-span-4 md:col-span-2 lg:col-span-4 flex items-end justify-end mb-10 gap-3">
            <button
              onClick={() => scrollTeam(-1)}
              disabled={teamIndex === 0}
              className="w-10 h-10 border border-gray-300 flex items-center justify-center text-blackNew disabled:opacity-30 hover:bg-gray-50 transition-colors"
              aria-label="Previous"
            >
              ←
            </button>
            <button
              onClick={() => scrollTeam(1)}
              disabled={teamIndex === teamMembers.length - 1}
              className="w-10 h-10 border border-gray-300 flex items-center justify-center text-blackNew disabled:opacity-30 hover:bg-gray-50 transition-colors"
              aria-label="Next"
            >
              →
            </button>
          </div>
        </div>

        {/* Scroll track */}
        <div className={containerClasses.container}>
          <div
            ref={teamScrollRef}
            className="col-span-4 md:col-span-8 lg:col-span-12 flex overflow-x-auto gap-6 no-scrollbar"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {teamMembers.map(member => (
              <div
                key={member.id}
                data-card
                className="flex-shrink-0 team-card"
              >
                {/* Portrait image */}
                <div
                  className="relative w-full bg-gray-100 mb-5 overflow-hidden"
                  style={{ aspectRatio: "2/3" }}
                >
                  {member.image ? (
                    <img
                      src={member.image}
                      alt={member.name}
                      className="absolute inset-0 w-full h-full object-cover object-top"
                      loading="lazy"
                      style={member.imgStyle}
                    />
                  ) : (
                    <>
                      <svg
                        className="absolute inset-0 w-full h-full"
                        preserveAspectRatio="none"
                      >
                        <line
                          x1="0"
                          y1="0"
                          x2="100%"
                          y2="100%"
                          stroke="#d1d5db"
                          strokeWidth="1"
                        />
                        <line
                          x1="100%"
                          y1="0"
                          x2="0"
                          y2="100%"
                          stroke="#d1d5db"
                          strokeWidth="1"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <p className="text-xs font-mono tracking-widest uppercase text-gray-500 bg-gray-100 px-2 py-1 mb-0">
                          Photo
                        </p>
                      </div>
                    </>
                  )}
                </div>
                {/* Info */}
                {member.tag && (
                  <span className="mb-2 inline-block font-mono text-xs tracking-wider uppercase px-2 py-1 border border-solid border-gray-300 text-gray-500">
                    {member.tag}
                  </span>
                )}
                <h3 className="mt-0 mb-1 fluid-heading-03">{member.name}</h3>
                <p className="mb-0 text-sm text-gray-500">{member.role}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Dots */}
        <div className={containerClasses.container}>
          <div className="col-span-4 md:col-span-8 lg:col-span-12 flex gap-2 mt-8">
            {teamMembers.map((_, i) => (
              <button
                key={i}
                onClick={() => {
                  setTeamIndex(i)
                  const container = teamScrollRef.current
                  const card = container?.querySelector("[data-card]")
                  if (card)
                    container.scrollTo({
                      left: i * (card.offsetWidth + 24),
                      behavior: "smooth",
                    })
                }}
                className={`w-2 h-2 rounded-full transition-colors ${
                  teamIndex === i ? "bg-blackNew" : "bg-gray-300"
                }`}
                aria-label={`Go to ${teamMembers[i].name}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Team reviews — verbatim Google review excerpts mentioning the team */}
      <div
        {...revealProps("teamReviews")}
        className="py-16 bg-white lg:py-24 reveal-on-scroll"
      >
        <div className={`${containerClasses.container}`}>
          <div className="col-span-4 md:col-span-8 lg:col-span-12 mb-10">
            <p className="mt-0 overline">In Their Words</p>
            <h2 className="mt-3 mb-0 fluid-heading-06">
              The team, <i>reviewed by patients</i>
            </h2>
          </div>
          <div className="col-span-4 md:col-span-8 lg:col-span-12 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {teamReviews.map(review => (
              <div
                key={review.quote}
                className="flex flex-col border border-solid border-gray-300 p-8"
              >
                <div
                  className="flex"
                  aria-label="Five star rating"
                  role="img"
                >
                  <img loading="lazy" className="h-4 -mr-1" src={starReviewGold} alt="" />
                  <img loading="lazy" className="h-4 -mr-1" src={starReviewGold} alt="" />
                  <img loading="lazy" className="h-4 -mr-1" src={starReviewGold} alt="" />
                  <img loading="lazy" className="h-4 -mr-1" src={starReviewGold} alt="" />
                  <img loading="lazy" className="h-4" src={starReviewGold} alt="" />
                </div>
                <p className="mt-6 mb-6 text-gray-600 leading-relaxed">
                  &ldquo;{review.quote}&rdquo;
                </p>
                <div className="mt-auto border-t border-solid border-gray-300 pt-4">
                  <p className="mb-0 text-sm text-right text-gray-500">
                    {review.name} &middot; Google review, {review.when}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Professor Collin */}
      <div
        {...revealProps("collin")}
        className="py-16 bg-white2 lg:py-24 reveal-on-scroll"
      >
        <div className={`${containerClasses.container}`}>
          <div className="col-span-4 md:col-span-4 lg:col-span-5 lg:col-start-1 flex flex-col justify-center">
            <p className="mt-0 overline">Where It Began</p>
            <h2 className="mt-3 mb-6 fluid-heading-06">
              Trained by the pioneer of <i>oculoplastic surgery</i>
            </h2>
            <p className="mt-0 mb-0 text-gray-600 leading-relaxed">
              Dr Hawkes was mentored for over a decade by Professor Richard
              Collin, widely regarded as the pioneer of Ophthalmic Plastic
              Surgery. She is honoured to continue his legacy, having taken over
              the care of his patients and carrying forward the exceptional
              standards he set throughout his career.
            </p>
          </div>
          <div className="col-span-4 md:col-span-4 lg:col-span-6 lg:col-start-7 self-stretch mt-8 lg:mt-0">
            <div
              className="relative w-full overflow-hidden"
              style={{ aspectRatio: "1/1" }}
            >
              <img
                src={elizabethSurgicalScrubs}
                alt="Dr Elizabeth Hawkes in surgical scrubs"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Locations */}
      <div {...revealProps("locations")} className="reveal-on-scroll">
        <ConsultingLocations />
      </div>

      <div {...revealProps("cta")} className="reveal-on-scroll">
        <CTABlock />
      </div>
      <div {...revealProps("affiliates")} className="reveal-on-scroll">
        <AffiliateList />
      </div>
    </Layout>
  )
}

export default AboutClinic
